import { Controller } from "@hotwired/stimulus"

// Collapses a dashboard section into the one-row-tall strip rendered by
// budgets/_section_deck_strip, and deals it back open.
//
// The open section is the shipped one — same header, same card, same rows —
// so this controller owns only the transition between the two states:
//
//   deal   the tiles in the collapsed strip's fan fly to the tiles in the rows
//          they belong to. Clones parked on the section do the flying, so the
//          reveal's overflow clipping cannot cut a tile mid-air while the
//          flight still stays inside the section and scrolls with it. Every
//          real tile is revealed together, once the last clone has landed.
//   glide  the title and count are the same label in both states, so they
//          travel the difference between the two positions rather than
//          cross-fading. That is what makes it read as one object opening.
//
// Both halves of the shell animate on grid-template-rows (0fr↔1fr), so no
// height is ever measured — see the .deck-* rules in budgets/index.
//
// The open/closed choice is remembered locally, per kind of section and
// across every budget: the dashboard is the screen the user lands on, a
// section they closed staying closed is the whole point of being able to
// close it, and having to close it again on each budget would not be a
// preference so much as a chore.
//
// Usage:
//   <div class="deck-open"
//        data-controller="section-deck"
//        data-section-deck-scope-value="subscriptions">
//     <div class="deck-strip-wrap">…<span data-section-deck-target="fan">…</span>…</div>
//     <div class="deck-body">…<div class="deck-header">…</div>…</div>
//   </div>
// One choice per kind of section, shared across every budget: collapsing
// Subscriptions collapses Subscriptions on every panel the carousel holds,
// and leaves All transactions alone. The scope value is what a section
// belongs to ("subscriptions", "recent-expenses"), never which budget it is
// on — the budget is the thing this deliberately ignores.
const STORAGE_PREFIX = "floosyo:section-deck:"
const SYNC_EVENT = "section-deck:state"
// The splay each tile sits at, per size of deck, matching the .deck-fan rules
// in budgets/index. A flight has to start (and end) on exactly this, or the
// hand-off between the real tile and its flying clone shows as a jump — so a
// two-row section leaning its tiles at ±7° needs the flight to know that, not
// the ±9° a three-row one uses.
const FAN_SPLAY = {
  1: [ { rotate: 0, y: 0 } ],
  2: [ { rotate: -7, y: 2 }, { rotate: 7, y: 2 } ],
  3: [ { rotate: -9, y: 2 }, { rotate: 0, y: 0 }, { rotate: 9, y: 2 } ]
}
const MAX_FAN_TILES = 3
// Money saved's tiles land inside the hero card, well below the medallion row,
// so they cover roughly twice the distance the other two sections' do. At the
// standard timings that reads as a lot of movement for a section that is
// showing two rows. `calm` shortens the flight, tightens the stagger and drops
// the arc, so the tiles go more or less straight there.
// The deepest tile in the fan leaves first, then the one in front of it, then
// the one on top. That tile has the furthest to travel, so starting it last
// left the deck sitting still for a beat while the near tiles had already
// arrived; deepest-first spreads the arrivals evenly.
// Opening and closing are separate profiles: closing is shorter and tighter,
// because a section going away should never hold the user up the way one
// arriving earns the right to. `arc` is the sideways bow of the flight path.
const FLIGHT_PROFILES = {
  standard: {
    open: { duration: 660, stagger: 55, arc: 12 },
    close: { duration: 480, stagger: 40, arc: 10 }
  },
  calm: {
    open: { duration: 470, stagger: 45, arc: 2 },
    close: { duration: 470, stagger: 45, arc: 2 }
  }
}
// Slow out of rest on open; a quicker, accelerating start on close.
const FLIGHT_EASING = { open: "cubic-bezier(.4,0,.1,1)", close: "cubic-bezier(.45,0,.2,1)" }
// A tile turned back mid-air is already moving, so it skips the stagger and
// the crouch and takes a shorter, decelerating path home.
const REVERSE_EASING = "cubic-bezier(.2,.6,.2,1)"
const REVERSE_DURATION_FACTOR = 0.8
// Open only: the tile dips 1.5px and shrinks to .97 over the first 12% of the
// flight before it leaves (anticipation).
const CROUCH = { share: 0.12, drop: 1.5, scale: 0.97 }
// Samples along the curved path. Nine keeps the bow smooth without handing
// WAAPI a long keyframe list.
const ARC_SAMPLES = 9
// Rotation reaches the target at 80% of the path, overshoots by 1.2deg and
// settles as the tile lands (follow-through).
const ROTATE_LEAD = 0.8
const ROTATE_OVERSHOOT_DEG = 1.2
// Up to 3% stretch along the direction of travel at mid-flight.
const FLIGHT_STRETCH = 0.03
// Row text starts once its own tile is about a quarter of the way there,
// and its stagger is half the tiles', so titles show without waiting.
const ROW_TEXT_LEAD = 0.275
const ROW_TEXT_STAGGER_FACTOR = 0.5
// Closing, rows leave bottom first, this far apart.
const ROW_EXIT_STAGGER_MS = 30
const GLIDE = {
  open: { duration: 520, easing: "cubic-bezier(.32,.72,0,1)" },
  close: { duration: 440, easing: "cubic-bezier(.45,0,.2,1)" },
  // The count badge trails the title by this much.
  stagger: 30,
  fromOpacity: 0.5
}
// A hair of motion blur on the tile while it travels, peaking mid-path.
// Enough to take the hard edge off a fast-moving card, not enough to read as
// soft focus.
const FLIGHT_BLUR_PX = 0.8
// The fan's own stacking, so a tile that leads the deck also leads it in the
// air. Clones are appended in order, where the last one would otherwise paint
// on top — the exact inverse of the fan — and the deck would appear to
// re-stack itself the moment the flight landed.
const FLYER_Z_BASE = 60
// Where a tile lands. Every icon inside the open section, not just the ones in
// an .expense-row: Money saved holds its skips in the hero card's own list, at
// 28px rather than 38px — which the flight already handles, since it scales
// each tile from the size it leaves at to the size it arrives at.
const ROW_TILES = ".deck-body .expense-icon-tile"

export default class extends Controller {
  static targets = ["fan", "stripLabel", "headerLabel"]
  static values = { scope: String, flight: String }

  connect() {
    this.flights = []
    this.glides = []
    this.generation = 0
    this.rectCache = {}

    // Turbo caches the DOM as it stands. A flight that was still in the air
    // when the user navigated away would be baked into the snapshot.
    this.clearFlyers = () => this.cancelFlights()
    document.addEventListener("turbo:before-cache", this.clearFlyers)

    // Another section on the page (or on a panel the carousel is hiding) was
    // toggled: follow it. On screen that means the same deal, off screen it is
    // applied flat — there is nothing to watch, and the flights would measure
    // against a display:none panel.
    this.onSync = (event) => {
      if (event.detail.origin === this) return
      if (event.detail.scope !== this.scopeValue) return
      this.applyState(event.detail.state, { animate: this.isVisible })
    }
    document.addEventListener(SYNC_EVENT, this.onSync)

    // The section is rendered open, so only a stored "closed" is work to do —
    // and it lands flat, because it is where the user left the dashboard
    // rather than something that just happened.
    this.applyState(this.storedState(), { animate: false })
  }

  disconnect() {
    document.removeEventListener(SYNC_EVENT, this.onSync)
    document.removeEventListener("turbo:before-cache", this.clearFlyers)
    this.clearFlyers()
  }

  toggle(event) {
    // Only two things carry this action: the collapsed strip, and the chevron
    // on the open header. Nothing else in the header toggles — a title that
    // makes the list vanish when tapped is a trap.
    if (event.target.closest("a")) return

    const next = this.isOpen ? "closed" : "open"
    this.applyState(next, { animate: true })
    this.store(next)
    document.dispatchEvent(new CustomEvent(SYNC_EVENT, {
      detail: { state: next, scope: this.scopeValue, origin: this }
    }))
  }

  applyState(state, { animate }) {
    const wantOpen = state === "open"
    if (wantOpen === this.isOpen) return

    if (animate) return wantOpen ? this.open() : this.close()

    this.cancelFlights()
    this.element.classList.add("deck-noanim")
    this.element.classList.remove("deck-dealing", "deck-undealing")
    this.element.classList.toggle("deck-open", wantOpen)
    requestAnimationFrame(() => this.element.classList.remove("deck-noanim"))
  }

  // A panel the budget carousel is holding off screen is display:none, so it
  // has no offsetParent and nothing worth animating.
  get isVisible() {
    return Boolean(this.element.offsetParent)
  }

  get isOpen() {
    return this.element.classList.contains("deck-open")
  }

  // ── state ────────────────────────────────────────────────────────────────

  // Sections start open: a user who has never collapsed this kind of section
  // should see the dashboard they have always seen.
  storedState() {
    try {
      return localStorage.getItem(STORAGE_PREFIX + this.scopeValue) || "open"
    } catch (_) {
      return "open"
    }
  }

  store(state) {
    try {
      localStorage.setItem(STORAGE_PREFIX + this.scopeValue, state)
    } catch (_) {
      // Private mode, quota — the section still works, it just forgets.
    }
  }

  // ── open / close ─────────────────────────────────────────────────────────

  open() {
    if (this.reducedMotion) {
      this.element.classList.add("deck-open")
      return
    }

    const rows = this.rowTiles()
    // Both ends are measured the same way — section forced into the state the
    // rect belongs to, splay off — so the flight starts and ends on the tile
    // itself rather than on a rotated bounding box.
    const origins = this.rects(".deck-fan .expense-icon-tile", false)
    const targets = this.rects(ROW_TILES, true)
    const labels = this.labelRects(true)

    // Tiles still in the air from a close turn back from where they are.
    const live = this.cancelFlights({ capture: true })
    this.element.classList.remove("deck-undealing")
    this.element.classList.add("deck-open", "deck-dealing")
    this.glide(true, labels)
    this.setRowDelays(true)

    // The fan's own tiles go too: the clones cover them exactly, so leaving
    // them up only risks a shrinking ghost showing through as the deck departs.
    const fans = this.fanTiles()
    fans.forEach((tile) => { tile.style.visibility = "hidden" })

    const flights = []
    rows.forEach((tile, index) => {
      const from = live[index] || origins[index]
      if (!from || !targets[index]) return
      tile.style.visibility = "hidden"
      const splayFrom = live[index] ? live[index].pose : this.splay(index)
      flights.push(this.fly(tile, from, targets[index], index, splayFrom, null, true))
    })

    this.handOff(flights, () => {
      rows.forEach((tile) => { tile.style.visibility = "" })
      fans.forEach((tile) => { tile.style.visibility = "" })
      this.element.classList.remove("deck-dealing")
    })
  }

  close() {
    if (this.reducedMotion) {
      this.element.classList.remove("deck-open")
      return
    }

    const rows = this.rowTiles()
    const origins = this.rects(ROW_TILES, true)
    const targets = this.rects(".deck-fan .expense-icon-tile", false)
    const labels = this.labelRects(false)

    // Tiles still in the air from an open turn back from where they are.
    const live = this.cancelFlights({ capture: true })
    this.element.classList.remove("deck-open", "deck-dealing")
    // The card fades as the reveal closes over it, so the strip coming up
    // underneath is a cross-fade rather than a cut. Dropped again once the
    // tiles are home, which is also what puts the card back at full opacity
    // for the next open. It also selects the closing timings in the CSS.
    this.element.classList.add("deck-undealing")
    this.glide(false, labels)
    this.setRowDelays(false)

    const fans = this.fanTiles()
    fans.forEach((tile) => { tile.style.visibility = "hidden" })

    const flights = []
    rows.forEach((tile, index) => {
      const from = live[index] || origins[index]
      if (!from || !targets[index]) return
      // The clone is the tile now; leaving the row's own copy up shows it
      // twice while the reveal is still closing over it.
      tile.style.visibility = "hidden"
      const splayFrom = live[index] ? live[index].pose : null
      flights.push(this.fly(tile, from, targets[index], index, splayFrom, this.splay(index), false))
    })

    this.handOff(flights, () => {
      fans.forEach((tile) => { tile.style.visibility = "" })
      rows.forEach((tile) => { tile.style.visibility = "" })
      this.element.classList.remove("deck-undealing")
    })
  }

  // Each row's text starts when its own tile is ROW_TEXT_LEAD of the way
  // there, so text follows the tile rather than a fixed top-down clock.
  // Closing, rows leave bottom first. Read by the .deck-* rules as --deck-row-in
  // and --deck-row-out.
  setRowDelays(opening) {
    const rows = [...this.element.querySelectorAll(".deck-body .expense-row")]
    const profile = this.profile(opening)
    const count = Math.min(rows.length, Math.max(this.fanTiles().length, 1))

    rows.forEach((row, index) => {
      const slot = Math.min(index, count - 1)
      const depth = Math.max(0, count - 1 - slot)
      const inMs = depth * profile.stagger * ROW_TEXT_STAGGER_FACTOR + profile.duration * ROW_TEXT_LEAD
      const outMs = (count - 1 - slot) * ROW_EXIT_STAGGER_MS
      row.style.setProperty("--deck-row-in", `${Math.round(inMs)}ms`)
      row.style.setProperty("--deck-row-out", `${Math.max(0, outMs)}ms`)
    })
  }

  profile(opening) {
    const set = FLIGHT_PROFILES[this.flightValue] || FLIGHT_PROFILES.standard
    return opening ? set.open : set.close
  }

  // Every tile is handed back to the page in the same frame, once the last one
  // has landed — never one at a time as each flight ends.
  //
  // Staggered hand-offs are what made the deck flicker: a tile returned to the
  // page sits in the section's own stacking context, at z-index 1-3, while the
  // tiles still in the air are clones at z-index 60+. On collapse the three
  // converge into one 70px fan, so for as long as one is still flying it paints
  // over the ones already home, and the deck appears to re-stack itself.
  handOff(flights, reveal) {
    if (!flights.length) return reveal()

    const generation = this.generation
    const landings = flights.map((flight) => flight.animation.finished.catch(() => {}))

    Promise.all(landings).then(() => {
      // A toggle that started while these were in the air owns the tiles now.
      if (generation !== this.generation) return

      reveal()
      // One frame with clone and tile both up, on identical geometry, so the
      // swap itself cannot show a gap.
      requestAnimationFrame(() => {
        if (generation !== this.generation) return
        flights.forEach((flight) => this.removeFlight(flight))
      })
    })
  }

  // Drops the clones and gives every tile back at once. With `capture`, it
  // first reads where each clone is in the air right now, so a toggle that
  // lands mid-flight reverses each tile from that exact spot instead of
  // snapping it back to the start. Returns those live rects by tile index.
  cancelFlights({ capture = false } = {}) {
    const live = capture ? this.livePoses() : []

    this.generation = (this.generation || 0) + 1
    this.flights.forEach(({ node, animation }) => {
      animation.cancel()
      node.remove()
    })
    this.flights = []
    this.fanTiles().forEach((tile) => { tile.style.visibility = "" })
    this.rowTiles().forEach((tile) => { tile.style.visibility = "" })
    return live
  }

  // A clone is positioned only by its transform, pinned at the section's
  // origin with the default centre transform-origin, so its centre sits at
  // (size / 2) + translation. Scale and rotation come off the matrix.
  livePoses() {
    const live = []
    if (!this.flights.length) return live

    const host = this.element.getBoundingClientRect()
    this.flights.forEach(({ node, index }) => {
      const matrix = new DOMMatrix(getComputedStyle(node).transform)
      const width = parseFloat(node.style.width)
      const height = parseFloat(node.style.height)
      const scale = Math.hypot(matrix.a, matrix.b)
      const rotate = Math.atan2(matrix.b, matrix.a) * 180 / Math.PI
      const centerX = width / 2 + matrix.e
      const centerY = height / 2 + matrix.f
      const liveWidth = width * scale
      const liveHeight = height * scale
      live[index] = {
        left: host.left + centerX - liveWidth / 2,
        top: host.top + centerY - liveHeight / 2,
        width: liveWidth,
        height: liveHeight,
        pose: { rotate, y: 0 },
        live: true
      }
    })
    return live
  }

  removeFlight(flight) {
    flight.node.remove()
    this.flights = this.flights.filter((candidate) => candidate !== flight)
  }

  // ── motion ───────────────────────────────────────────────────────────────

  // Rects for a state the section is not in yet. Measured fresh while the
  // section is at rest, and remembered relative to the section. While anything
  // is still moving, the remembered rects are used instead: measuring flips
  // the section under .deck-noanim, which cancels the grid-rows transition
  // that is still running, and that cancel is what made a tap mid-toggle snap.
  rects(selector, wantOpen) {
    const key = `${selector}|${wantOpen}`
    const host = this.element.getBoundingClientRect()

    if (!this.isMoving || !this.rectCache[key]) {
      this.rectCache[key] = this.measure(selector, wantOpen).map((rect) => ({
        x: rect.left - host.left,
        y: rect.top - host.top,
        width: rect.width,
        height: rect.height
      }))
    }

    return this.rectCache[key].map((rect) => ({
      left: host.left + rect.x,
      top: host.top + rect.y,
      width: rect.width,
      height: rect.height
    }))
  }

  get isMoving() {
    return this.flights.length > 0 ||
      this.glides.length > 0 ||
      this.element.classList.contains("deck-dealing") ||
      this.element.classList.contains("deck-undealing")
  }

  // Rects for elements that are not in the state we need them in yet: flip to
  // that state with every transition off, read, flip back.
  measure(selector, wantOpen) {
    const was = this.isOpen
    this.element.classList.add("deck-noanim", "deck-measuring")
    this.element.classList.toggle("deck-open", wantOpen)

    const rects = [...this.element.querySelectorAll(selector)].map((el) => el.getBoundingClientRect())

    this.element.classList.toggle("deck-open", was)
    void this.element.offsetWidth
    this.element.classList.remove("deck-noanim", "deck-measuring")
    return rects
  }

  // The fan splay mirrors under RTL, the same way the CSS does.
  splay(index) {
    const deck = FAN_SPLAY[this.fanTiles().length] || FAN_SPLAY[MAX_FAN_TILES]
    const splay = deck[index] || { rotate: 0, y: 0 }
    const rtl = document.documentElement.getAttribute("dir") === "rtl"
    return { rotate: rtl ? -splay.rotate : splay.rotate, y: splay.y }
  }

  // `from` and `to` are the flat tile rects. `splayFrom` / `splayTo` carry the
  // fan's rotation and 2px drop for whichever end of the flight is the fan;
  // the row end passes null, because a row tile is flat. Returns the clone and
  // its animation — landing is handled together, in handOff.
  fly(source, from, to, index, splayFrom, splayTo, opening) {
    const node = source.cloneNode(true)
    node.classList.add("deck-flyer")
    node.style.visibility = ""
    node.style.zIndex = String(FLYER_Z_BASE + MAX_FAN_TILES - index)
    // Pinned to the section's origin, with the whole position carried by the
    // transform below. Splitting it — left/top for the origin, transform for
    // the travel — is what left a tile a pixel out at the end: a composited
    // layer's static offset is snapped to a whole pixel, while its transform
    // is subpixel-exact, so a tile whose real position is fractional (they all
    // are, inside a flex row) flew the whole path off by that fraction and
    // corrected itself the moment the real tile came back.
    node.style.left = "0"
    node.style.top = "0"
    node.style.width = `${from.width}px`
    node.style.height = `${from.height}px`
    // Parented to the section, not the page: a flight belongs to the section
    // that started it and must never be painted over a different one.
    this.element.appendChild(node)

    // Index 0 is the tile on top of the fan; the last one is the deepest, and
    // it goes first. Mirrors correctly under RTL on its own, because depth is
    // stacking order, not a side of the screen. A tile turned back mid-air
    // has no stagger: it is already moving.
    const profile = this.profile(opening)
    const depth = Math.max(0, this.fanTiles().length - 1 - index)
    const delay = from.live ? 0 : depth * profile.stagger
    const duration = from.live ? profile.duration * REVERSE_DURATION_FACTOR : profile.duration
    const easing = from.live ? REVERSE_EASING : (opening ? FLIGHT_EASING.open : FLIGHT_EASING.close)

    const start = splayFrom || { rotate: 0, y: 0 }
    const end = splayTo || { rotate: 0, y: 0 }
    const scale = from.width ? to.width / from.width : 1

    // Both rects are viewport coordinates; the clone is absolutely positioned
    // inside the section, so they are rebased onto the section's own box. The
    // section scrolls with the page, so the flight does too — no compensation
    // needed, and nothing pinned to the viewport.
    const host = this.element.getBoundingClientRect()
    const path = {
      fromX: from.left - host.left,
      fromY: from.top - host.top,
      toX: to.left - host.left,
      toY: to.top - host.top
    }

    const keyframes = this.arcFrames(path, start, end, scale, profile, opening && !from.live)
    const animation = node.animate(keyframes, { duration, delay, easing, fill: "both" })

    // The ring belongs to the fan, so it arrives and leaves with the flight:
    // fading in as the tiles converge into the deck, out as they spread into
    // rows. It settles at 80% of the path, so the tile is already wearing its
    // final edge by the time it touches down and the hand-off shows nothing.
    const ring = document.createElement("span")
    ring.className = "deck-flyer-ring"
    node.appendChild(ring)

    const towardsFan = Boolean(splayTo)
    ring.animate(
      [ { opacity: towardsFan ? 0 : 1 }, { opacity: towardsFan ? 1 : 0 } ],
      {
        duration: duration * 0.8,
        delay,
        easing: "cubic-bezier(.4,0,.6,1)",
        fill: "both"
      }
    )

    const flight = { node, animation, index }
    this.flights.push(flight)
    return flight
  }

  // The flight path as keyframes: a quadratic curve bowed sideways by
  // profile.arc (arcs), an optional crouch before leaving (anticipation),
  // rotation that arrives early and overshoots before settling
  // (follow-through), and a slight stretch along the path at speed.
  //
  // Composed in exactly the order the fan's CSS composes it —
  // `rotate(Xdeg) translateY(2px)` — because those 2px run along the tile's
  // rotated Y axis, not the page's. Translating first and rotating after
  // uses the same two numbers to land somewhere else: it drops the
  // horizontal component of the splay, 2px × sin(9°) = 0.31px, which is a
  // whole device pixel at 3x, on the two outer tiles only. Size still moves
  // monotonically: a scale that peaks and comes back reads as the tiles
  // shrinking into each other on collapse.
  arcFrames({ fromX, fromY, toX, toY }, start, end, scale, profile, crouch) {
    const frame = (x, y, rotate, splayY, scaleX, scaleY) =>
      `translate(${x}px, ${y}px) rotate(${rotate}deg) translateY(${splayY}px) scale(${scaleX}, ${scaleY})`

    const dx = toX - fromX
    const dy = toY - fromY
    const length = Math.hypot(dx, dy) || 1
    const rtl = document.documentElement.getAttribute("dir") === "rtl"
    const bow = profile.arc * (crouch ? -1 : 1) * (rtl ? -1 : 1)
    const controlX = (fromX + toX) / 2 + (-dy / length) * bow
    const controlY = (fromY + toY) / 2 + (dx / length) * bow

    const lead = crouch ? CROUCH.share : 0
    const turn = end.rotate - start.rotate
    const overshoot = Math.abs(turn) < 0.5 ? 0 : Math.sign(turn) * ROTATE_OVERSHOOT_DEG
    const frames = []

    if (crouch) {
      frames.push({ transform: frame(fromX, fromY, start.rotate, start.y, 1, 1), filter: "blur(0px)", offset: 0 })
      frames.push({
        transform: frame(fromX, fromY + CROUCH.drop, start.rotate, start.y, CROUCH.scale, CROUCH.scale),
        filter: "blur(0px)",
        offset: lead
      })
    }

    for (let step = crouch ? 1 : 0; step <= ARC_SAMPLES; step++) {
      const t = step / ARC_SAMPLES
      const u = 1 - t
      const x = u * u * fromX + 2 * u * t * controlX + t * t * toX
      const y = u * u * fromY + 2 * u * t * controlY + t * t * toY
      let rotate = start.rotate + turn * Math.min(1, t / ROTATE_LEAD)
      if (t > 0.6 && t < 1) rotate += overshoot * Math.sin(((t - 0.6) / 0.4) * Math.PI)
      const size = 1 + (scale - 1) * t
      const stretch = 1 + FLIGHT_STRETCH * Math.sin(t * Math.PI)
      frames.push({
        transform: frame(x, y, rotate, start.y + (end.y - start.y) * t, size / Math.sqrt(stretch), size * stretch),
        filter: `blur(${(Math.sin(t * Math.PI) * FLIGHT_BLUR_PX).toFixed(2)}px)`,
        offset: lead + (1 - lead) * t
      })
    }
    return frames
  }

  // The label exists twice — once on the strip, once on the header — and only
  // one of them is on screen at a time. Whichever is arriving starts from
  // where the other one sat.
  glide(opening, rects) {
    // A label still gliding from the previous toggle starts the new glide
    // from where it is right now, so a reversal never jumps.
    const departing = opening ? this.stripLabelTargets : this.headerLabelTargets
    const liveFrom = this.glides.length ? departing.map((el) => el.getBoundingClientRect()) : null
    this.glides.forEach((animation) => animation.cancel())
    this.glides = []

    const arriving = opening ? this.headerLabelTargets : this.stripLabelTargets
    const from = liveFrom || rects.from
    const to = rects.to
    const timing = opening ? GLIDE.open : GLIDE.close

    arriving.forEach((el, index) => {
      if (!from[index] || !to[index]) return
      const dx = from[index].left - to[index].left
      const dy = from[index].top - to[index].top
      if (!dx && !dy) return

      const animation = el.animate([
        { transform: `translate(${dx}px, ${dy}px)`, opacity: GLIDE.fromOpacity },
        { transform: "none", opacity: 1 }
      ], { duration: timing.duration, delay: index * GLIDE.stagger, easing: timing.easing, fill: "both" })

      // Dropped the moment it lands. `fill: both` holds the final frame, and a
      // held animation keeps its element composited for good — every toggle
      // leaving another one behind, and the header's crisp glyphs (the
      // chevron next to these labels) rasterising a pixel off their laid-out
      // position. The final frame is `transform: none; opacity: 1`, which is
      // the element's own base style, so cancelling changes nothing on screen.
      this.glides.push(animation)
      animation.finished.then(() => this.dropGlide(animation)).catch(() => {})
    })
  }

  // Read before the section changes state, while it can still be measured at
  // rest (see rects).
  labelRects(opening) {
    const strip = this.rects("[data-section-deck-target='stripLabel']", false)
    const header = this.rects("[data-section-deck-target='headerLabel']", true)
    return opening ? { from: strip, to: header } : { from: header, to: strip }
  }

  dropGlide(animation) {
    animation.cancel()
    this.glides = this.glides.filter((candidate) => candidate !== animation)
  }

  fanTiles() {
    return this.hasFanTarget ? [...this.fanTarget.querySelectorAll(".expense-icon-tile")] : []
  }

  rowTiles() {
    return [...this.element.querySelectorAll(ROW_TILES)]
  }

  get reducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }
}
